import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Lock, ShieldCheck } from "lucide-react";
import { fetchProduct } from "../lib/api";
import MenuBack from "../components/MenuBack";
import ProductCard from "../components/ProductCard";
import MissionArt from "../components/MissionArt";
import { getMission, isMissionLocked } from "../content/missions";

// A single mission file: the story, then the gear tied to it.
export default function CampaignDetail() {
  const { slug } = useParams();
  const mission = getMission(slug);
  const [product, setProduct] = useState(null);
  const [gearState, setGearState] = useState("loading"); // loading | ready | inbound | error

  const productSlug = mission && !isMissionLocked(mission) ? mission.productSlug : null;

  useEffect(() => {
    setProduct(null);
    if (!productSlug) {
      setGearState("inbound");
      return;
    }
    setGearState("loading");
    let cancelled = false;
    fetchProduct(productSlug)
      .then((data) => {
        if (cancelled) return;
        setProduct(data);
        setGearState("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        // Only a 404 means the shirt isn't in the store yet; anything else is
        // an outage, so don't tell people the gear doesn't exist.
        setGearState(err?.response?.status === 404 ? "inbound" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [productSlug]);

  if (!mission || isMissionLocked(mission)) {
    return (
      <div className="bg-[#06080C] text-white min-h-screen">
        <MenuBack to="/campaigns" label="Mission Select" />
        <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-5 py-16">
          {mission && <MissionArt mission={mission} className="w-40 aspect-square mb-6" />}
          {!mission && <Lock className="w-10 h-10 text-[#8A91A0] mb-4" />}
          <div className="font-mono text-xs uppercase tracking-[0.35em] text-[#C54A4A]">
            {mission ? "Access Denied" : "File Not Found"}
          </div>
          <p className="mt-3 text-[#A0A6B5] max-w-md">
            {mission
              ? "This mission file is classified. Check back soon."
              : "No mission file matches that code."}
          </p>
          <Link
            to="/campaigns"
            className="mt-6 inline-flex items-center gap-2 border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] px-6 py-3 font-mono uppercase tracking-[0.3em] text-[11px] transition-colors"
          >
            Return to Mission Select
          </Link>
        </div>
      </div>
    );
  }

  const accent = mission.accent;

  return (
    <div className="bg-[#06080C] text-white min-h-screen">
      <MenuBack to="/campaigns" label="Mission Select" trail={`Campaign · Mission ${mission.code}`} />

      {/* Mission file header */}
      <section className="border-b border-[#1F2330] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{ background: `radial-gradient(circle at 80% 40%, ${accent}33 0%, transparent 60%)` }}
        />
        <div className="absolute inset-0 warning-stripes opacity-15" />

        <div className="relative px-5 md:px-12 py-12 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A0A6B5] mb-2">
              Mission {mission.code} // Mission File
            </div>
            <h1
              data-testid="campaign-name"
              className="font-display text-5xl sm:text-7xl uppercase leading-none tracking-[0.05em]"
              style={{ color: accent }}
            >
              {mission.name}
            </h1>
            <p className="mt-3 text-[#A0A6B5] text-lg max-w-2xl">{mission.tagline}</p>
            <div
              className="mt-5 flex items-center gap-2 font-mono uppercase text-[10px] tracking-[0.3em] border border-[#2A3040] bg-[#11141C] px-3 py-1.5 inline-flex"
              style={{ color: accent }}
            >
              <ShieldCheck className="w-3 h-3" /> Status: Active
            </div>
          </div>
          <MissionArt mission={mission} className="w-48 md:w-72 aspect-square shrink-0 justify-self-center md:justify-self-auto" />
        </div>
      </section>

      {/* Briefing */}
      <section className="max-w-4xl mx-auto px-5 md:px-10 py-14 space-y-10">
        <div className="border border-[#1F2330] bg-[#0A0D14] p-6 md:p-10 corners relative">
          <div className="label mb-4" style={{ color: accent }}>/ Mission Briefing</div>
          <div className="space-y-4 text-[#A0A6B5] leading-relaxed text-base">
            {mission.briefing.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {mission.objectives?.length > 0 && (
            <>
              <div className="label mt-8 mb-3" style={{ color: accent }}>/ Objectives</div>
              <ul className="space-y-2">
                {mission.objectives.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="font-mono text-[10px] uppercase tracking-widest mt-1" style={{ color: accent }}>
                      [0{i + 1}]
                    </span>
                    <span className="text-[#A0A6B5]">{b}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Gear */}
        <div data-testid="mission-gear">
          <div className="label mb-2" style={{ color: accent }}>/ Mission Gear</div>
          <h2 className="font-display text-2xl md:text-3xl uppercase tracking-[0.05em] mb-6">
            Gear Up
          </h2>
          {gearState === "ready" && product ? (
            <div className="grid sm:grid-cols-2 gap-6 items-center">
              <ProductCard product={product} divisionAccent={accent} />
              <div>
                <p className="text-[#A0A6B5] text-sm leading-relaxed">{product.short}</p>
                <Link
                  to={`/product/${product.slug}`}
                  className="mt-5 inline-flex items-center gap-2 border px-6 py-3 font-mono uppercase tracking-[0.3em] text-[11px] transition-colors hover:text-black hover:bg-[var(--accent)]"
                  style={{ borderColor: accent, color: accent, "--accent": accent }}
                >
                  Equip <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-[#2A3040] bg-[#0A0D14] p-8 md:p-10 text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.35em]" style={{ color: accent }}>
                {gearState === "loading"
                  ? "Loading loadout…"
                  : gearState === "error"
                    ? "Signal Lost"
                    : "Gear Inbound"}
              </div>
              {gearState === "error" && (
                <p className="mt-3 text-sm text-[#A0A6B5]">
                  Mission gear couldn't be loaded right now. Refresh to try again.
                </p>
              )}
              {gearState === "inbound" && (
                <p className="mt-3 text-sm text-[#A0A6B5]">
                  The {mission.name} shirt is being prepped for deployment. Join
                  the list in the footer to get the drop first.
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
